import { clamp01, frac, smoothstep } from '../../shared/scalar';
import { hash } from '../hash';

const BEATS_PER_BAR = 4;

export const SLIP = { frames: 8, loopBeats: 16, share: 0.35 };

export function slipped(frame: number): boolean {
  return hash(frame, 7) < SLIP.share;
}

export const ARC = { beats: 0.25, flickers: 8, kinks: 3, reach: 15, sway: 4, blank: 1.4 };
const WILD = { from: 0.6, step: 0.4, flashBeats: 0.3 };

export function arcSize(beats: number, sinceStart: number | undefined, blank: boolean): number {
  if (blank) {
    return ARC.blank;
  }
  if (sinceStart !== undefined && sinceStart >= -BEATS_PER_BAR && sinceStart < 0) {
    return WILD.from + WILD.step * (Math.floor(sinceStart + BEATS_PER_BAR) + 1);
  }
  return frac(beats) < ARC.beats ? 1 : 0;
}

export function flash(sinceStart: number): number | undefined {
  return sinceStart >= 0 && sinceStart < WILD.flashBeats ? sinceStart / WILD.flashBeats : undefined;
}

export function arcKinks(
  beats: number,
  prong: number,
  size = 1,
): readonly (readonly [number, number])[] {
  const flicker = Math.floor(beats * ARC.flickers);
  return Array.from({ length: ARC.kinks }, (_, index) => {
    const up = ((index + 1) / ARC.kinks) * ARC.reach * size;
    const aside =
      (hash(flicker, prong * 10 + index) - 0.5) * 2 * ARC.sway * size * (1 + index * 0.5);
    return [aside, -up] as const;
  });
}

export function fuse(sinceStart: number): number | undefined {
  if (sinceStart < -BEATS_PER_BAR || sinceStart >= 0) {
    return undefined;
  }
  const into = sinceStart + BEATS_PER_BAR;
  return (Math.floor(into) + smoothstep(clamp01(frac(into) * 3))) / BEATS_PER_BAR;
}

export const TRIP_BEATS = 0.6;

export interface Drain {
  readonly amount: number;
  readonly spread?: number;
  readonly reveal?: number;
}

export const DRAIN = { spread: 0.5, calm: 1, back: 1, dark: 0.25 };

export function drain(
  sinceStart: number,
  sinceEnd: number | undefined,
  calm: boolean,
): Drain | undefined {
  if (sinceEnd !== undefined && sinceEnd >= 0) {
    const p = sinceEnd / DRAIN.back;
    if (p >= 1) {
      return undefined;
    }
    return calm ? { amount: 1 - p } : { amount: 1, reveal: p };
  }
  if (sinceStart < 0) {
    return undefined;
  }
  if (calm) {
    return { amount: clamp01(sinceStart / DRAIN.calm) };
  }
  const spread = sinceStart / DRAIN.spread;
  return spread < 1 ? { amount: 1, spread } : { amount: 1 };
}

export function returnRings(sinceEnd: number): readonly { share: number; alpha: number }[] {
  const p = clamp01(sinceEnd / DRAIN.back);
  if (sinceEnd < 0 || p >= 1) {
    return [];
  }
  return [
    { share: p, alpha: 1 - p },
    { share: p * 0.7, alpha: (1 - p) * 0.8 },
  ];
}
